import {
  useWorkflowExecutionMutation,
  WorkflowExecutionApiArg,
} from "../../../features/workflowTriggers/workflowExecution-api";
import React, {useCallback, useMemo, useState} from "react";
import {useAppSelector} from "../../../store";
import {useWorkflowIds} from "../../../common/OrganizationConfig/useWorkflowIds";
import {useModalForm} from "../../../common/hooks/useModalForm";
import Grid from "@mui/material/Unstable_Grid2";
import {OrderGQLDto} from "../../../features/order/order-api";
import {Button, Typography, useMediaQuery, useTheme} from "@mui/material";
import {getFormattedPrice} from "../../../../utils/formatCurrency.utils";
import {Field, FieldArray, Form, Formik} from "formik";
import {TextField} from "formik-mui";
import * as yup from "yup";
import {LoadingButton} from "@mui/lab";
import {Trans} from "react-i18next";
import {toastError} from "../../../common/utils/toastMessages";
import {t} from "i18next";
import {decimal2PrecisionRegex} from "../../../Purchases/EditPurchases/pages/validation";

const i18nKeyPrefix = "parcelShipments.shipmentDetails.addItemsInsurance.";

export function useAddItemsInsurance(
  {
    jobType,
    parcelShipment,
    reloadParcelShipment,
  }: {
    jobType: string;
    parcelShipment: OrderGQLDto | null;
    reloadParcelShipment: (() => Promise<void>) | null;
  } = {
    jobType: "",
    parcelShipment: null,
    reloadParcelShipment: null,
  },
) {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  const config = useAppSelector(
    (state: any) => state.organizationConfigState.modules.parcelTracking,
  );

  const isAllowed = useMemo(() => {
    const parcelStatusName = parcelShipment?.orderStatus?.orderStatusName;
    return (
      config?.allowedAddInsuranceJobTypes &&
      jobType &&
      config.allowedAddInsuranceJobTypes.some((x: string) => x === jobType) &&
      config?.allowedAddInsuranceParcelStatuses &&
      parcelStatusName &&
      config.allowedAddInsuranceParcelStatuses.includes(parcelStatusName)
    );
  }, [config, jobType, parcelShipment]);

  const [runWorkflow] = useWorkflowExecutionMutation();

  const {updateShipmentItemsInsuranceWorkflowId} = useWorkflowIds();

  const executeWorkflow = useCallback(
    async (values: any) => {
      if (!isAllowed) {
        throw new Error(
          "Adding items insurance is not allowed for this job type.",
        );
      }

      const executeQuoteWorkflowApiArgs: WorkflowExecutionApiArg = {
        organizationId: process.env
          .REACT_APP_PORTAL_ORGANIZATION_ID as unknown as number,
        workflowId: updateShipmentItemsInsuranceWorkflowId,
        values: {variables: {purchases: values.purchases}},
      };

      const response = await runWorkflow(executeQuoteWorkflowApiArgs).unwrap();

      if (response.error) {
        toastError(t("toasts.serverError"));
        console.error(`Workflow execution failed: ${response.error.message}`);
        return response;
      }

      if (reloadParcelShipment) {
        await reloadParcelShipment();
      }
      setShowModal(false);

      return response;
    },
    [runWorkflow],
  );

  const [showModal, setShowModal] = useState(false);

  const openButton = useMemo(() => {
    if (!isAllowed) return null;
    return (
      <Button
        variant={"outlined"}
        color={"primary"}
        sx={{
          height: "32px",
          minWidth: "max-content",
        }}
        onClick={() => setShowModal(true)}
      >
        <Trans i18nKey={i18nKeyPrefix + "addInsurance"}>Add insurance</Trans>
      </Button>
    );
  }, [isAllowed, setShowModal]);

  const {renderModal} = useModalForm({
    isOpen: showModal && isAllowed,
    modalStyle: {
      minWidth: "min(90vw, 1800px)",
      transform: "translate(-50%, calc(-50vh + 1rem))",
      overflowY: "auto",
      marginY: 4,
    },
    content: (
      <Grid textAlign={"left"}>
        <Formik
          initialValues={{
            purchases:
              parcelShipment?.shipments
                ?.filter((shipment) => shipment.orderType === "Purchase")
                ?.sort(
                  (purchase1, purchase2) =>
                    (purchase1.orderEntityUltimateConsignee?.[0]?.contact
                      ?.contactId ?? 0) -
                    (purchase2.orderEntityUltimateConsignee?.[0]?.contact
                      ?.contactId ?? 0),
                )
                .map((purchase) => {
                  const client =
                    purchase?.orderEntityUltimateConsignee?.[0]?.contact;
                  return {
                    orderId: purchase.orderId,
                    trackingNumber: purchase.trackingNumber,
                    client: [
                      client?.contactFirstName,
                      client?.contactLastName,
                      client?.accountNumber && `(${client.accountNumber})`,
                    ]?.join(" "),
                    commodities:
                      purchase.commodities?.flatMap(
                        (item) =>
                          item.commodity?.containerCommodities?.map(
                            (containerCommodity) => ({
                              commodityId: containerCommodity.commodityId,
                              description: containerCommodity.description,
                              quantity: containerCommodity.quantity,
                              unitaryValue: containerCommodity.unitaryValue,
                              insuredTotalAmount:
                                containerCommodity.customValues
                                  ?.insuredTotalAmount,
                            }),
                          ) || [],
                      ) || [],
                  };
                }) || [],
          }}
          onSubmit={async (values, {setSubmitting}) => {
            setSubmitting(true);
            try {
              if (!parcelShipment) return;
              await executeWorkflow(values);
            } catch (error) {
              console.error("Error executing workflow:", error);
            } finally {
              setSubmitting(false);
            }
          }}
          validationSchema={yup.object({
            purchases: yup.array().of(
              yup.object({
                commodities: yup.array().of(
                  yup.object({
                    insuredTotalAmount: yup
                      .string()
                      .matches(
                        decimal2PrecisionRegex,
                        t("validation.common.decimal2Precision") ?? "",
                      ),
                  }),
                ),
              }),
            ),
          })}
        >
          {({values, isSubmitting}) => (
            <Form>
              <Grid display={"flex"} flexDirection={"column"} gap={2}>
                <Typography variant="h3">
                  <Trans i18nKey={i18nKeyPrefix + "goodsInParcel"}>
                    Goods in Parcel
                  </Trans>{" "}
                  {parcelShipment?.trackingNumber}
                </Typography>
                <Grid container spacing={2} display={{md: "flex", xs: "none"}}>
                  <Grid xs={12} md={3}>
                    <Typography variant="body4">
                      <Trans i18nKey={i18nKeyPrefix + "purchaseTrackingNumber"}>
                        Purchase Tracking Number
                      </Trans>
                    </Typography>
                  </Grid>
                  <Grid xs={12} md={2}>
                    <Typography variant="body4">
                      <Trans i18nKey={i18nKeyPrefix + "client"}>Client</Trans>
                    </Typography>
                  </Grid>
                  <Grid xs={12} md={2}>
                    <Typography variant="body4">
                      <Trans i18nKey={i18nKeyPrefix + "item"}>Item</Trans>
                    </Typography>
                  </Grid>
                  <Grid xs={12} md={1}>
                    <Typography variant="body4">
                      <Trans i18nKey={i18nKeyPrefix + "quantity"}>
                        Quantity
                      </Trans>
                    </Typography>
                  </Grid>
                  <Grid xs={12} md={2}>
                    <Typography variant="body4">
                      <Trans i18nKey={i18nKeyPrefix + "declaredValueTotal"}>
                        Declared Value Total
                      </Trans>
                    </Typography>
                  </Grid>
                  <Grid xs={12} md={2}>
                    <Typography variant="body4">
                      <Trans i18nKey={i18nKeyPrefix + "insuredTotalAmount"}>
                        Insured Total Amount
                      </Trans>
                    </Typography>
                  </Grid>
                </Grid>
                <Grid>
                  <FieldArray name="purchases">
                    {() =>
                      values.purchases?.map((purchase, purchaseIndex) => (
                        <Grid
                          container
                          key={purchase.orderId}
                          sx={{mb: 3}}
                          spacing={2}
                        >
                          <Grid xs={12} md={3} mt={2}>
                            {isDesktop ? (
                              purchase.trackingNumber
                            ) : (
                              <Typography variant={"h3"}>
                                <Trans i18nKey={i18nKeyPrefix + "purchase"}>
                                  Purchase
                                </Trans>
                                : {purchase.trackingNumber}
                              </Typography>
                            )}
                          </Grid>
                          <Grid xs={12} md={2} mt={{md: 2, xs: 0}}>
                            {isDesktop ? (
                              purchase.client
                            ) : (
                              <Typography
                                variant={"h3"}
                                fontSize={{xs: "1rem"}}
                              >
                                <Trans i18nKey={i18nKeyPrefix + "client"}>
                                  Client
                                </Trans>
                                : {purchase.client}
                              </Typography>
                            )}
                          </Grid>
                          <Grid xs={12} md={7}>
                            {purchase.commodities.map(
                              (commodity, commodityIndex) => {
                                const totalValue =
                                  (commodity.unitaryValue || 0) *
                                  (commodity.quantity || 0);
                                const totalValueFormatted =
                                  getFormattedPrice(totalValue);
                                return (
                                  <Grid
                                    container
                                    columns={{xs: 12, md: 7}}
                                    key={commodity.commodityId}
                                    alignItems={"center"}
                                    mb={1}
                                    spacing={2}
                                  >
                                    {!isDesktop && (
                                      <Grid xs={12}>
                                        <Typography
                                          variant="h3"
                                          fontSize={{xs: "1rem"}}
                                        >
                                          <Trans
                                            i18nKey={i18nKeyPrefix + "item"}
                                          >
                                            Item
                                          </Trans>{" "}
                                          {commodityIndex + 1}
                                        </Typography>
                                      </Grid>
                                    )}
                                    <Grid md={2} xs={12}>
                                      {commodity.description}
                                    </Grid>
                                    <Grid md={1} xs={12}>
                                      {commodity.quantity}{" "}
                                      <Trans i18nKey={"pieces"}>pcs</Trans>
                                    </Grid>
                                    <Grid md={2} xs={12}>
                                      {isDesktop ? (
                                        totalValueFormatted
                                      ) : (
                                        <Typography>
                                          <Trans
                                            i18nKey={
                                              i18nKeyPrefix +
                                              "declaredValueTotal"
                                            }
                                          >
                                            Declared Value Total
                                          </Trans>
                                          : {totalValueFormatted}
                                        </Typography>
                                      )}
                                    </Grid>
                                    <Grid md={2} xs={12}>
                                      <Field
                                        type="hidden"
                                        name={`purchases[${purchaseIndex}].commodities[${commodityIndex}].commodityId`}
                                      />
                                      <Field
                                        fullWidth
                                        component={TextField}
                                        data-testid={`input-purchase-item-insuredTotalAmount-${purchaseIndex}-${commodityIndex}`}
                                        name={`purchases[${purchaseIndex}].commodities[${commodityIndex}].insuredTotalAmount`}
                                        variant="outlined"
                                        label={
                                          isDesktop ? undefined : (
                                            <Trans
                                              i18nKey={
                                                i18nKeyPrefix +
                                                "insuredTotalAmount"
                                              }
                                            >
                                              Insured Total Amount
                                            </Trans>
                                          )
                                        }
                                        size={isDesktop ? "normal" : "small"}
                                        placeholder={getFormattedPrice(0)}
                                      />
                                    </Grid>
                                  </Grid>
                                );
                              },
                            )}
                          </Grid>
                        </Grid>
                      ))
                    }
                  </FieldArray>
                </Grid>
              </Grid>
              <Grid
                display={"flex"}
                justifyContent={"flex-end"}
                flexWrap={"wrap-reverse"}
                gap={2}
                mt={2}
              >
                <Button
                  variant="outlined"
                  color="primary"
                  size={"small"}
                  onClick={() => setShowModal(false)}
                  data-testid="modal-cancel"
                  disabled={isSubmitting}
                >
                  <Trans i18nKey={"modalForm.default.cancel"}>Cancel</Trans>
                </Button>
                <LoadingButton
                  variant="contained"
                  color="secondary"
                  type="submit"
                  data-testid="modal-confirm"
                  size={"small"}
                  loading={isSubmitting}
                >
                  <Trans i18nKey={"modalForm.default.confirm"}>Save</Trans>
                </LoadingButton>
              </Grid>
            </Form>
          )}
        </Formik>
      </Grid>
    ),
  });

  return {
    renderModal,
    isAllowed,
    openButton,
  };
}
